/**
 * StandingsExample — a worked example under a points, tie-breaker or knockout
 * rule while it is being edited, so organisers see what their numbers do.
 * The tie-breaker table re-sorts as the order changes. (The real table is
 * worked out on the server — backend/services/standings_rules.py.)
 */
const fmt = (n) => (Number.isInteger(n) ? String(n) : String(Number(n.toFixed(2))));
const ord = (n) => `${n}${['th', 'st', 'nd', 'rd'][(n % 100 > 10 && n % 100 < 14) || n % 10 > 3 ? 0 : n % 10]}`;

// Three teams level on 6 points
const SAMPLE = [
  { name: 'Falcons', wins: 3, nrr: 0.42, runs: 12, h2h: 2 },
  { name: 'Hawks', wins: 2, nrr: 1.1, runs: 25, h2h: 0 },
  { name: 'Kites', wins: 3, nrr: -0.2, runs: -5, h2h: 4 },
];
const KEY = { wins: 'wins', nrr: 'nrr', runs_difference: 'runs', head_to_head: 'h2h' };
const COLUMN = { wins: 'Wins', nrr: 'NRR', runs_difference: 'Run diff', head_to_head: 'Head-to-head pts' };
const DECIDER = { super_over: 'super over', bowl_out: 'bowl-out', toss: 'toss' };

function PointsExample({ p }) {
  const win = p.win ?? 2;
  const tie = p.tie === 'shared' ? win / 2 : (p.tie ?? 1);
  const loss = p.loss ?? 0;
  return (
    <p>
      A team that wins 3, ties 1 and loses 1 has <strong>{fmt(3 * win + tie + loss)} points</strong>
      {p.tie === 'shared' && <> — a tie shares the win, {fmt(tie)} each</>}.
      {p.walkover !== undefined && p.walkover !== null && <> A walkover is worth {fmt(p.walkover)}.</>}
    </p>
  );
}

function TieBreakerExample({ order }) {
  const auto = [];
  let decider = null;
  for (const b of order || []) {
    if (DECIDER[b]) { decider = b; break; }
    if (KEY[b]) auto.push(b);
  }
  const rows = [...SAMPLE].sort((a, b) => {
    for (const k of auto) {
      if (a[KEY[k]] !== b[KEY[k]]) return b[KEY[k]] - a[KEY[k]];
    }
    return 0;
  });
  const level = (i) => i > 0 && auto.every((k) => rows[i][KEY[k]] === rows[i - 1][KEY[k]]);
  return (
    <>
      <p className="mb-1.5">Falcons, Hawks and Kites all finish on 6 points:</p>
      <table className="w-full" data-testid="tie-breaker-example">
        <thead className="text-gray-500">
          <tr>
            <th className="text-left font-semibold">#</th><th className="text-left font-semibold">Team</th>
            {auto.map((k) => <th key={k} className="font-semibold text-right">{COLUMN[k]}</th>)}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={r.name} className="text-[#2B3A67]">
              <td>{level(i) ? '=' : ord(i + 1)}</td><td className="font-bold">{r.name}</td>
              {auto.map((k) => <td key={k} className="text-right tabular-nums">{k === 'nrr' ? r.nrr.toFixed(2) : r[KEY[k]]}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
      {decider && rows.some((_, i) => level(i)) && <p className="mt-1.5">Teams still level play a {DECIDER[decider]}.</p>}
    </>
  );
}

function KnockoutExample({ p }) {
  const pairs = p.semi_pairing === 'neighbours' ? '1st v 2nd and 3rd v 4th' : '1st v 4th and 2nd v 3rd';
  if (p.format === 'qualifier_eliminator') {
    return <p>Qualifier 1: 1st v 2nd — the winner is in the final. Eliminator: 3rd v 4th. Qualifier 2: the loser of qualifier 1 v the eliminator winner, for the other final place.</p>;
  }
  if (p.format === 'top_two_final') return <p>The final: 1st v 2nd.</p>;
  if (p.format === 'semi_finals') {
    if (p.teams === 3) return <p>1st goes straight to the final; 2nd v 3rd for the other place.</p>;
    if (p.teams === 2) return <p>The final: 1st v 2nd.</p>;
    return <p>Semi-finals: {pairs}; the winners play the final.</p>;
  }
  if (p.format === 'cups_by_position') {
    const cups = p.cups && Object.keys(p.cups).length ? Object.entries(p.cups) : null;
    if (!cups) return <p>1st v 2nd play the Cup final, 3rd v 4th the Plate final, 5th v 6th the Bowl final, and so on.</p>;
    let start = 1;
    const bands = cups.map(([name, n]) => { const b = `${name}: ${ord(start)}–${ord(start + n - 1)}`; start += n; return b; });
    return (
      <p>
        {bands.join(' · ')}. A cup of 2 plays a final, 3 gives the top team a place in the final, 4 plays semi-finals ({pairs}).
        Other sizes are arranged by the organiser.
      </p>
    );
  }
  if (p.format === 'custom') return <p>You arrange the knockout matches yourself; explain the format in your own words.</p>;
  return null;
}

export default function StandingsExample({ block, params }) {
  const p = params || {};
  let body = null;
  if (block === 'match_points') body = <PointsExample p={p} />;
  else if (block === 'tie_breakers' && p.order?.length) body = <TieBreakerExample order={p.order} />;
  else if (block === 'knockout_format' && p.format) body = <KnockoutExample p={p} />;
  if (!body) return null;
  return (
    <div className="mt-4 rounded-xl bg-[#E6F5F4] p-3 text-xs text-[#1F7A75] leading-relaxed" data-testid="standings-example">
      <p className="font-bold mb-1">For example</p>
      {body}
    </div>
  );
}
